export const VERSION = '0.1.0'

/**
 * Semver-ish comparison. Returns >0 if a>b, 0 if equal, <0 if a<b.
 *
 * Strips pre-release (-rc.1) and build metadata (+sha.abc) suffixes before
 * comparing — for the toolkit's purposes "1.0.0-rc.1" and "1.0.0" are the
 * same release. Within a segment, numeric strings compare numerically;
 * non-numeric strings compare lexically (so "alpha" < "beta"). Mixed
 * numeric/non-numeric at the same position fall back to lexical on the
 * raw segment text.
 */
export function compareVersions(a: string, b: string): number {
  const strip = (v: string) => String(v).split(/[-+]/)[0]
  const pa = strip(a).split('.')
  const pb = strip(b).split('.')
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const sa = pa[i] ?? '0'
    const sb = pb[i] ?? '0'
    const na = parseInt(sa, 10)
    const nb = parseInt(sb, 10)
    const aIsNum = !Number.isNaN(na) && String(na) === sa
    const bIsNum = !Number.isNaN(nb) && String(nb) === sb
    if (aIsNum && bIsNum) {
      if (na !== nb) return na - nb
    } else {
      // At least one is non-numeric → lexical compare on raw segment text.
      if (sa !== sb) return sa < sb ? -1 : 1
    }
  }
  return 0
}

/**
 * Resolve the latest published version from the remote registry.
 *
 * Default registry is the npm registry (`https://registry.npmjs.org`) using
 * the package's own `name` field from package.json. Override via `registry`
 * option or `HARNESS_TOOLKIT_REGISTRY` env var. Network failures degrade
 * silently to `null` — the caller (update-check) treats null as "unknown"
 * and falls back to the local-only comparison signal.
 */
export interface ResolveRemoteLatestOptions {
  fetch?: typeof fetch
  registry?: string
  timeoutMs?: number
}

interface PackageMetadata {
  version?: unknown
  'dist-tags'?: { latest?: unknown } | null
}

const DEFAULT_REGISTRY = 'https://registry.npmjs.org'

export async function resolveRemoteLatest(
  opts: ResolveRemoteLatestOptions = {},
): Promise<string | null> {
  const packageName = '@wefq1981/harness-toolkit'
  const registryBase =
    opts.registry ??
    process.env.HARNESS_TOOLKIT_REGISTRY ??
    DEFAULT_REGISTRY
  const url = `${registryBase.replace(/\/$/, '')}/${encodeURIComponent(packageName)}`
  const timeoutMs = opts.timeoutMs ?? 5000
  const fetchImpl = opts.fetch ?? fetch

  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    const res = await fetchImpl(url, {
      signal: controller.signal,
      headers: { 'accept': 'application/json' },
    })
    clearTimeout(timer)
    if (!res.ok) {
      return null
    }
    const json = (await res.json()) as PackageMetadata
    const latestViaDistTags =
      json?.['dist-tags'] && typeof json['dist-tags'].latest === 'string'
        ? json['dist-tags'].latest
        : null
    if (latestViaDistTags) {
      return latestViaDistTags
    }
    if (typeof json?.version === 'string') {
      return json.version
    }
    return null
  } catch {
    return null
  }
}
